import { memo } from 'react';
import { Popover } from 'antd';
import HeaderSortDescChevron from 'assets/svg/HeaderSortDescChevron';
import { TrendArrowDown } from 'assets/svg/TrendArrowDown';
import TrendArrowUp from 'assets/svg/TrendArrowUp';
import WhitespaceItemTooltip, { WhitespaceTooltipItem } from './WhitespaceItemTooltip';
import {
    formatWhitespaceCellKpiValue,
    getWhitespaceCellKpiSharePercentage,
    type WhitespaceCellKpi,
} from 'hooks/useWhitespaceAnalysisGridData';
import { formatNumWithKMB } from 'utils/helperMethods';

const THIN_SCROLLBAR_CLASS =
    '[scrollbar-width:thin] [scrollbar-color:#a1a1aa_transparent] [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-[#a1a1aa] [&::-webkit-scrollbar-track]:bg-transparent';
const ITEM_NAME_CHAR_LIMIT = 20;
/**
 * Item chips live inside nested scrollable containers (card list, grid body). Without
 * alwaysByViewport, antd/rc-align measures overflow against those clipped ancestors instead of
 * the real window, so the popover never flips upward near the bottom of the page.
 */
const POPOVER_AUTO_ADJUST_OVERFLOW = { adjustX: 1, adjustY: 1, alwaysByViewport: true } as const;
/** Inset 2px stroke — same look as border without changing layout (box-shadow, not border-width). */
const WHITESPACE_HIGHLIGHT_CLASS =
    'shadow-[inset_0_0_0_2px_#18181b,0_4px_6px_-2px_rgba(0,0,0,0.03),0_10px_15px_-3px_rgba(0,0,0,0.07)]';

const truncateLabel = (label?: string) => {
    const value = label ?? '-';
    return value.length > ITEM_NAME_CHAR_LIMIT ? `${value.slice(0, ITEM_NAME_CHAR_LIMIT)}...` : value;
};

const getShareDisplayValue = (shareValue: string) =>
    shareValue === '-' || shareValue.endsWith('%') ? shareValue : `${shareValue}%`;

const getShareDisplayLabel = (ownShareKpi: WhitespaceCellKpi | null) =>
    ownShareKpi?.display_label ?? ownShareKpi?.kpi_name ?? 'Own Value Share';

const getCardContainerClassName = (highlightWhitespace: boolean, dimNonWhitespace: boolean) =>
    `flex h-full w-full flex-col items-start gap-2 self-stretch p-[16px] ${
        highlightWhitespace ? WHITESPACE_HIGHLIGHT_CLASS : ''
    } ${dimNonWhitespace ? 'opacity-15' : ''}`;

type WhitespaceTrendBadgeProps = {
    trendValue: number | null;
    trendDirection: 'up' | 'down' | 'neutral';
};

const WhitespaceTrendBadge = ({ trendValue, trendDirection }: WhitespaceTrendBadgeProps) => {
    if (typeof trendValue !== 'number' || trendValue === 0) {
        return null;
    }

    const isDown = trendDirection === 'down';
    const colorClass = isDown ? 'text-[#d92d20]' : 'text-[#007a41]';
    const arrowColor = isDown ? '#d92d20' : '#007a41';

    return (
        <span className={`text-[16px] leading-[20px] ${colorClass}`}>
            <span className="inline-flex items-center gap-0.5">
                {isDown ? (
                    <TrendArrowDown className="shrink-0" color={arrowColor} />
                ) : (
                    <TrendArrowUp className="shrink-0" color={arrowColor} />
                )}
                <span>{`${formatNumWithKMB(Math.abs(trendValue), 1)}%`}</span>
            </span>
        </span>
    );
};

type WhitespaceItemChipProps = {
    item: WhitespaceTooltipItem;
    selectedYAxisKpiId?: string;
    yAxisAttributeLabel?: string;
    yAxisAttributeValue?: string;
};

const WhitespaceItemChip = ({
    item,
    selectedYAxisKpiId,
    yAxisAttributeLabel,
    yAxisAttributeValue,
}: WhitespaceItemChipProps) => (
    <Popover
        trigger="hover"
        placement="rightTop"
        autoAdjustOverflow={POPOVER_AUTO_ADJUST_OVERFLOW}
        showArrow={false}
        overlayInnerStyle={{ padding: 0, background: 'transparent', boxShadow: 'none' }}
        content={
            <WhitespaceItemTooltip
                item={item}
                selectedYAxisKpiId={selectedYAxisKpiId}
                yAxisAttributeLabel={yAxisAttributeLabel}
                yAxisAttributeValue={yAxisAttributeValue}
            />
        }
    >
        <span
            className={`w-fit cursor-pointer rounded px-1 py-[1px] text-xs bg-white ${
                item.is_own_attribute
                    ? 'shadow-[3px_3px_3px_rgba(0,0,0,0.12),4px_4px_4px_rgba(0,0,0,0.05)]'
                    : 'opacity-70'
            }`}
        >
            {truncateLabel(item.description)}
        </span>
    </Popover>
);

type WhitespaceCardActionsProps = {
    showCollapseButton: boolean;
    moreCount: number;
    isMoreLoading: boolean;
    onCollapseClick?: () => void;
    onMoreClick?: () => void;
};

const WhitespaceCardActions = ({
    showCollapseButton,
    moreCount,
    isMoreLoading,
    onCollapseClick,
    onMoreClick,
}: WhitespaceCardActionsProps) => {
    if (!showCollapseButton && moreCount <= 0) {
        return null;
    }

    return (
        <div className="mt-2 flex items-center gap-3 text-xs text-[#71717b]">
            {showCollapseButton ? (
                <button
                    type="button"
                    onClick={onCollapseClick}
                    className="flex items-center gap-1 border-none bg-transparent p-0 text-xs text-[#71717b]"
                >
                    <span>Collapse</span>
                    <span className="inline-flex rotate-180">
                        <HeaderSortDescChevron color={'#71717B'} />
                    </span>
                </button>
            ) : null}
            {moreCount > 0 ? (
                <button
                    type="button"
                    onClick={onMoreClick}
                    disabled={isMoreLoading}
                    className="flex items-center gap-1 border-none bg-transparent p-0 text-xs text-[#71717b] disabled:opacity-60"
                >
                    <span>{`+ ${moreCount} more`}</span>
                    <HeaderSortDescChevron color={'#71717B'} />
                </button>
            ) : null}
        </div>
    );
};

export type StaticWhitespaceCardProps = {
    valueSales: string;
    valuePrefix: string;
    valueSuffix: string;
    trendValue: number | null;
    trendDirection: 'up' | 'down' | 'neutral';
    avgValueSales: string;
    avgPrefix: string;
    avgSuffix: string;
    ownShareKpi: WhitespaceCellKpi | null;
    crossSectionShareLabel: string;
    crossSectionLabel?: string;
    items: WhitespaceTooltipItem[];
    moreCount: number;
    heatmapColor: string;
    onMoreClick?: () => void;
    isMoreLoading?: boolean;
    onCollapseClick?: () => void;
    showCollapseButton?: boolean;
    isWhitespace?: boolean;
    showWhitespaces?: boolean;
    selectedYAxisKpiId?: string;
    /** Y-axis attribute label (e.g. "Size Range") - only set when the Y-axis is an attribute bucket, not a KPI. */
    yAxisAttributeLabel?: string;
    /** This row's bucket value for the Y-axis attribute (e.g. "500ml-1L"). */
    yAxisAttributeValue?: string;
    onCellHover?: () => void;
    onCellHoverEnd?: () => void;
};

const StaticWhitespaceCard = memo(
    ({
        valueSales,
        valuePrefix,
        valueSuffix,
        trendValue,
        trendDirection,
        avgValueSales,
        avgPrefix,
        avgSuffix,
        ownShareKpi,
        items,
        moreCount,
        heatmapColor,
        onMoreClick,
        isMoreLoading = false,
        onCollapseClick,
        showCollapseButton = false,
        isWhitespace = false,
        showWhitespaces = false,
        selectedYAxisKpiId,
        yAxisAttributeLabel,
        yAxisAttributeValue,
        onCellHover,
        onCellHoverEnd,
    }: StaticWhitespaceCardProps) => {
        const shareValue = formatWhitespaceCellKpiValue(ownShareKpi);
        const sharePercentage = getWhitespaceCellKpiSharePercentage(ownShareKpi);
        const shareDisplayValue = getShareDisplayValue(shareValue);
        const shareDisplayLabel = getShareDisplayLabel(ownShareKpi);
        const highlightWhitespace = showWhitespaces && isWhitespace;
        const dimNonWhitespace = showWhitespaces && !isWhitespace;

        return (
            <div
                className={getCardContainerClassName(highlightWhitespace, dimNonWhitespace)}
                style={{ background: heatmapColor }}
                onMouseEnter={onCellHover}
                onMouseLeave={onCellHoverEnd}
            >
                <div className="flex w-full items-start justify-between">
                    <div className="leading-6 flex-1">
                        <div className="flex items-center gap-2">
                            <span className="text-[24px] font-bold text-blackTitle">{`${valuePrefix}${valueSales}${valueSuffix}`}</span>
                            <WhitespaceTrendBadge trendValue={trendValue} trendDirection={trendDirection} />
                        </div>
                        <div className="text-[16px] text-[#71717b]">{`Avg. ${avgPrefix}${avgValueSales}${avgSuffix}`}</div>
                    </div>
                </div>

                <div className="flex w-full flex-col gap-1">
                    <div className="flex w-full items-end justify-between font-medium text-[#3f3f46]">
                        <span className="text-[12px] leading-normal">
                            <span>{shareDisplayValue}</span>{' '}
                            <span className="text-[#71717b]">{shareDisplayLabel}</span>
                        </span>
                    </div>
                    <div className="box-border h-[4px] w-full overflow-hidden rounded-full border-[0.5px] border-solid border-[#e4e4e7] bg-white">
                        <div className="h-full rounded-full bg-[#3f3f46]" style={{ width: `${sharePercentage}%` }} />
                    </div>
                </div>

                <div className="mt-3 gap-1 w-full">
                    <div
                        className={`flex max-h-[250px] flex-col gap-1 overflow-y-auto pr-1 pb-[5px] ${THIN_SCROLLBAR_CLASS}`}
                    >
                        {items.map((item, index) => (
                            <WhitespaceItemChip
                                key={`${item.description ?? 'item'}-${index}`}
                                item={item}
                                selectedYAxisKpiId={selectedYAxisKpiId}
                                yAxisAttributeLabel={yAxisAttributeLabel}
                                yAxisAttributeValue={yAxisAttributeValue}
                            />
                        ))}
                    </div>
                    <WhitespaceCardActions
                        showCollapseButton={showCollapseButton}
                        moreCount={moreCount}
                        isMoreLoading={isMoreLoading}
                        onCollapseClick={onCollapseClick}
                        onMoreClick={onMoreClick}
                    />
                </div>
            </div>
        );
    },
);

export default StaticWhitespaceCard;
